<template>
  <div class="error-log">
    <v-alert v-if="error" type="error" variant="tonal" class="mb-4">
      {{ error }}
    </v-alert>

    <v-card variant="outlined" class="mb-4">
      <v-card-title>Лог ошибок</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.origin"
              :items="originOptions"
              label="Сервер"
              variant="outlined"
              density="compact"
              clearable
              hide-details
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.level"
              :items="levelOptions"
              label="Уровень"
              variant="outlined"
              density="compact"
              clearable
              hide-details
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="search"
              label="Поиск по ID, сообщению или файлу"
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              density="compact"
              clearable
              hide-details
            />
          </v-col>
          <v-col cols="12">
            <v-switch
              v-model="includeResolved"
              label="Показывать решённые и помеченные «не ошибка»"
              density="compact"
              hide-details
              @update:model-value="loadList"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-data-table
      :headers="headers"
      :items="groupedItems"
      :search="search"
      :custom-filter="groupMatchesSearch"
      :loading="loading"
      :items-per-page="25"
      @click:row="(event, { item }) => openDetail(item)"
    >
      <template #item.id="{ item }">
        #{{ item.id }}
        <div v-if="item.remoteid && item.remoteid !== item.id" class="text-medium-emphasis text-caption">
          источник #{{ item.remoteid }}
        </div>
      </template>
      <template #item.level="{ item }">
        <v-chip :color="levelColor(item.level)" size="small">{{ item.level }}</v-chip>
      </template>
      <template #item.message="{ item }">
        {{ item.message }}
        <span v-if="item.contexts > 1" class="text-medium-emphasis">
          (+{{ item.contexts - 1 }} {{ contextsWord(item.contexts - 1) }})
        </span>
      </template>
      <template #item.status="{ item }">
        <v-chip :color="statusColor(item.status)" size="small">{{ statusLabel(item.status) }}</v-chip>
      </template>
      <template #item.timemodified="{ item }">
        {{ formatDate(item.timemodified) }}
      </template>
      <template #item.actions="{ item }">
        <v-btn
          icon="mdi-delete"
          size="small"
          variant="text"
          color="error"
          @click.stop="confirmRemoveGroup(item)"
        />
      </template>
    </v-data-table>

    <error-detail
      :items="detailItems"
      @close="detailItems = []"
      @set-status="onSetStatus"
      @remove="onRemove"
    />
    <confirmdlg ref="confirm" />
  </div>
</template>

<script>
import confirmDialog from 'moodleDir/procollege/lib/vue/confirm-dialog.vue';
import errorDetail from './error-detail.vue';

const STATUS_LABELS = {
  0: 'К выполнению',
  1: 'В работе',
  2: 'Не ошибка',
  20: 'Не ошибка',
  100: 'Решено',
};

export default {
  name: "ErrorLog",
  components: {
    confirmdlg: confirmDialog,
    errorDetail,
  },
  data() {
    return {
      loading: false,
      error: "",
      items: [],
      detailItems: [],
      search: "",
      includeResolved: false,
      filters: {
        origin: null,
        level: null,
      },
      headers: [
        { title: "ID", key: "id", width: "6%" },
        { title: "Уровень", key: "level", width: "8%" },
        { title: "Сообщение", key: "message", width: "22%" },
        { title: "Файл", key: "filename", width: "18%" },
        { title: "Сервер", key: "origin", width: "12%" },
        { title: "Повторов", key: "occurrences", width: "7%" },
        { title: "Контекстов", key: "contexts", width: "7%" },
        { title: "Последний раз", key: "timemodified", width: "11%" },
        { title: "Статус", key: "status", width: "7%" },
        { title: "", key: "actions", sortable: false, width: "2%" },
      ],
    };
  },
  computed: {
    originOptions() {
      return [...new Set(this.items.map((item) => item.origin).filter(Boolean))].sort();
    },
    levelOptions() {
      return [...new Set(this.items.map((item) => item.level).filter(Boolean))];
    },
    filteredItems() {
      return this.items.filter((item) => {
        if (this.filters.origin !== null && item.origin !== this.filters.origin) return false;
        if (this.filters.level !== null && item.level !== this.filters.level) return false;
        return true;
      });
    },
    /**
     * Срабатывания в одном файле на одной строке — это одна и та же ошибка
     * кода, даже если сообщение и контекст запроса у каждого свои (hash в БД
     * строится по сообщению и потому для группировки не годится). В общей
     * таблице показываем такие срабатывания одной строкой; сообщения и
     * контексты каждого из них видны при открытии детального просмотра.
     */
    groupedItems() {
      const groups = new Map();

      for (const item of this.filteredItems) {
        let group = groups.get(item.groupKey);
        if (!group) {
          group = {
            groupKey: item.groupKey,
            ids: [],
            remoteids: [],
            filename: item.filename,
            origin: item.origin,
            occurrences: 0,
            statuses: [],
            timecreated: item.timecreated,
            timemodified: item.timemodified,
            id: item.id,
            remoteid: item.remoteid,
            level: item.level,
            message: item.message,
          };
          groups.set(item.groupKey, group);
        }

        group.ids.push(item.id);
        if (item.remoteid) group.remoteids.push(item.remoteid);
        group.occurrences += item.occurrences;
        group.statuses.push(item.status);
        group.timecreated = Math.min(group.timecreated, item.timecreated);

        // Заголовок группы (id/уровень/сообщение) — от самого свежего
        // срабатывания. Явное сравнение, а не расчёт на порядок сортировки
        // с сервера: он приоритизирован по уровню серьёзности, а не по времени.
        if (item.timemodified >= group.timemodified) {
          group.timemodified = item.timemodified;
          group.id = item.id;
          group.remoteid = item.remoteid;
          group.level = item.level;
          group.message = item.message;
        }
      }

      return [...groups.values()].map((group) => ({
        ...group,
        contexts: group.ids.length,
        status: this.aggregateStatus(group.statuses),
      }));
    },
  },
  created() {
    this.loadList();
  },
  methods: {
    async loadList() {
      this.loading = true;
      this.error = "";
      try {
        const response = await this.axios.post(
          `${this.$model.wwwroot}/procollege/manage_groups/errorlog/getList`,
          new URLSearchParams({ includeResolved: this.includeResolved ? "1" : "0" })
        );
        this.items = Array.isArray(response.data?.items) ? response.data.items : [];
      } catch (error) {
        this.error = "Не удалось загрузить лог ошибок.";
        if (this.axiosDefaultErrorHandler) this.axiosDefaultErrorHandler(error);
      } finally {
        this.loading = false;
      }
    },
    async openDetail(group) {
      this.error = "";
      try {
        const response = await this.axios.post(
          `${this.$model.wwwroot}/procollege/manage_groups/errorlog/getDetail`,
          new URLSearchParams({ ids: group.ids.join(",") })
        );
        this.detailItems = Array.isArray(response.data?.items) ? response.data.items : [];
      } catch (error) {
        this.error = "Не удалось загрузить подробности ошибки.";
        if (this.axiosDefaultErrorHandler) this.axiosDefaultErrorHandler(error);
      }
    },
    async onSetStatus({ id, status }) {
      try {
        const response = await this.axios.post(
          `${this.$model.wwwroot}/procollege/manage_groups/errorlog/setStatus`,
          new URLSearchParams({ id: String(id), status: String(status) })
        );
        if (response.data?.error) {
          this.error = response.data.error;
          return;
        }
        // Обновляем статус прямо в открытом диалоге, не закрывая его —
        // так видно результат, не теряя место в списке контекстов.
        const context = this.detailItems.find((el) => el.id === id);
        if (context) context.status = status;
        await this.loadList();
      } catch (error) {
        this.error = "Не удалось изменить статус.";
        if (this.axiosDefaultErrorHandler) this.axiosDefaultErrorHandler(error);
      }
    },
    async confirmRemoveGroup(group) {
      const question = group.contexts > 1
        ? `Удалить все ${group.contexts} срабатывания «${group.message}»?`
        : `Удалить запись «${group.message || group.filename}»?`;

      if (await this.$refs.confirm.open("Подтверждение", question)) {
        await this.removeIds(group.ids);
      }
    },
    async onRemove(id) {
      if (!(await this.$refs.confirm.open("Подтверждение", "Удалить это срабатывание?"))) {
        return;
      }

      await this.removeIds([id]);
      this.detailItems = this.detailItems.filter((el) => el.id !== id);
    },
    async removeIds(ids) {
      try {
        const endpoint = ids.length > 1 ? "removeGroup" : "remove";
        const params = ids.length > 1
          ? { ids: ids.join(",") }
          : { id: String(ids[0]) };

        await this.axios.post(
          `${this.$model.wwwroot}/procollege/manage_groups/errorlog/${endpoint}`,
          new URLSearchParams(params)
        );
        await this.loadList();
      } catch (error) {
        this.error = "Не удалось удалить запись.";
        if (this.axiosDefaultErrorHandler) this.axiosDefaultErrorHandler(error);
      }
    },
    aggregateStatus(statuses) {
      // Пока есть хоть одно неразобранное срабатывание — группа считается
      // требующей внимания, даже если часть уже отмечена решённой.
      if (statuses.includes(1)) return 1;
      if (statuses.includes(0)) return 0;
      if (statuses.includes(100)) return 100;
      return 2;
    },
    groupMatchesSearch(value, search, item) {
      if (!search) return true;
      const needle = search.toLocaleLowerCase();
      const raw = item.raw ?? item;
      return String(raw.id).includes(needle)
        || (Array.isArray(raw.ids) && raw.ids.some((id) => String(id).includes(needle)))
        || (Array.isArray(raw.remoteids) && raw.remoteids.some((id) => String(id).includes(needle)))
        || String(raw.message).toLocaleLowerCase().includes(needle)
        || String(raw.filename).toLocaleLowerCase().includes(needle);
    },
    contextsWord(count) {
      const mod10 = count % 10;
      const mod100 = count % 100;
      if (mod10 === 1 && mod100 !== 11) return "вариант";
      if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) return "варианта";
      return "вариантов";
    },
    levelColor(level) {
      if (level === "DEBUG" || level === "INFO") return "primary";
      if (level === "NOTICE" || level === "WARNING") return "orange";
      return "red";
    },
    statusLabel(status) {
      return STATUS_LABELS[status] ?? "К выполнению";
    },
    statusColor(status) {
      if (status === 100 || status === 2 || status === 20) return "success";
      if (status === 1) return "orange";
      return "grey";
    },
    formatDate(ts) {
      return ts ? new Date(ts * 1000).toLocaleString("ru") : "—";
    },
  },
};
</script>

<style scoped>
.error-log {
  width: 100%;
}
.error-log :deep(tbody tr) {
  cursor: pointer;
}
/* Фиксированная раскладка + перенос слов вместо горизонтальной прокрутки:
   вся информация должна быть видна без скролла, за счёт высоты строки. */
.error-log :deep(table) {
  table-layout: fixed;
  width: 100%;
}
.error-log :deep(th),
.error-log :deep(td) {
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}
</style>
